/** @module components/product-card */
import baustein from "baustein";
import environment from "web/script/modules/environment";
import analytics from "web/script/analytics/analytics";
import userProfiler from "web/script/modules/userprofiler";
import SaveForLaterSignupPrompt from "web/script/components/save-for-later-signup-prompt";
import SaveForLaterButton from "web/script/components/save-for-later-button";
import { languageIsEnglish } from "web/script/modules/language";
import productClickMixin from "./mixins/product-click-mixin";

import "web/script/components/lazy-image";
import "web/script/components/lead-link";
import "web/script/components/show-extra";
import "web/script/components/tracked-link";
import "web/script/components/wishlist-delete";

const IMAGE_CLICK_CLASSES = [".product-card__image-link"];
const DESCRIPTION_CLICK_CLASSES = [".product-card__short-description"];
export const SIGNUP_SELECTOR = ".product-card__save-for-later-signup-prompt";
export const SHORT_DESCRIPTION_SELECTOR = ".product-card__short-description-name";
export const AFFILIATE_SELECTOR = ".product-card__affiliate";

const EXPANDABLE_FULL = ".product-card__expandable-full";
const EXPANDABLE_TRUNCATED = ".product-card__expandable-truncated";
const EXPANDABLE_BTN_MORE = ".product-card__expand-btn-more";
const EXPANDABLE_BTN_LESS = ".product-card__expand-btn-less";
const HIDDEN = "hidden";

/**
 * The current product card component is only the beginning
 * More functionality incoming.
 * @constructor
 * @alias module:components/product-card
 * @extends module:baustein
 */
export default baustein.register(
    "product-card",
    /** @lends module:components/product-card.prototype */
    {
        mixins: [productClickMixin],

        defaultOptions: {
            newTabEnabled: false,
            overlayEnabled: false,
            productSlug: null,
            productId: null,
            productUid: null,
            productGender: null,
            reason: null,
            inStock: true,
            hasTooltip: false,
            linkId: null,
            useOverlayOnMobile: false,
        },

        setupEvents(add) {
            add("click", SIGNUP_SELECTOR, this.onAnonSaveForLater);
            add("click", SHORT_DESCRIPTION_SELECTOR, this.onClickShortDescription);
            add("click", AFFILIATE_SELECTOR, this.onClickAffiliateRetailer);

            // Experiment ORG-1791
            add("click", EXPANDABLE_BTN_MORE, this.toggleMoreLess);
            add("click", EXPANDABLE_BTN_LESS, this.toggleMoreLess);
        },

        onInsert() {
            if (!languageIsEnglish()) {
                // Don't render save for later button or signup prompt in i18n MVPs
                return;
            }

            this.renderOnlySaveForLater();
        },

        renderOnlySaveForLater() {
            const container = this.el.querySelector(".product-card__details");
            if (!container) {
                return;
            }
            const isWishListCard = this.el.classList.contains("wishlist-card");
            if (userProfiler.isLoggedIn()) {
                if (!this.findComponent("save-for-later-button") && !isWishListCard) {
                    const saveForLaterButton = this.getSaveForLaterButton();
                    saveForLaterButton.classList.add("product-card__save-for-later");
                    container.insertBefore(saveForLaterButton, container.firstElementChild);
                }
            } else if (!this.el.querySelector(".product-card__save-for-later-signup-prompt")) {
                const saveForLaterSignupPrompt = this.getSaveForLaterSignupPrompt();
                saveForLaterSignupPrompt.classList.add("product-card__save-for-later");
                container.insertBefore(saveForLaterSignupPrompt, container.firstElementChild);
            }
        },

        getSaveForLaterSignupPrompt() {
            const enableTooltip = environment.get("s4lFeedTooltip");
            const tooltipAdded = environment.get("s4lFeedTooltipAdded");

            if (enableTooltip && !tooltipAdded) {
                this.hasTooltip = true;
                environment.set({ s4lFeedTooltipAdded: true });
            }
            const prompt = new SaveForLaterSignupPrompt({
                has_tooltip: this.hasTooltip,
                productId: this.options.productId,
                productSlug: this.options.productSlug,
                productGender: this.options.productGender,
            });
            const wrapper = document.createElement("span");
            wrapper.className = "product-card__save-for-later-signup-prompt";
            prompt.appendTo(wrapper);

            return wrapper;
        },

        getSaveForLaterButton() {
            const button = new SaveForLaterButton({
                isLoveIcon: true,
                productId: this.options.productId,
            });
            const wrapper = document.createElement("span");
            button.appendTo(wrapper);

            return wrapper;
        },

        onAnonSaveForLater() {
            analytics.event("save_for_later", "add_item", "product_card");
            this.emit("productCardInteraction");
        },

        getClickOverlayClasses() {
            return IMAGE_CLICK_CLASSES.concat(DESCRIPTION_CLICK_CLASSES);
        },

        getClickRedirectClasses() {
            return [];
        },

        enable() {
            this.el.classList.remove("product-card--disabled");
        },

        disable() {
            this.el.classList.add("product-card--disabled");
        },

        onClickShortDescription() {
            analytics.event("product_card", "short description");
            this.emit("productCardInteraction");
        },

        onClickAffiliateRetailer() {
            analytics.event("product_card", "retailer");
            this.emit("productCardInteraction");
        },

        toggleMoreLess() {
            let fullText = this.el.querySelector(EXPANDABLE_FULL);
            let shortText = this.el.querySelector(EXPANDABLE_TRUNCATED);

            if (fullText.classList.contains(HIDDEN)) {
                fullText.classList.remove(HIDDEN);
                shortText.classList.add(HIDDEN);
                analytics.event("product_card", "expand more");
            } else {
                fullText.classList.add(HIDDEN);
                shortText.classList.remove(HIDDEN);
                analytics.event("product_card", "expand less");
            }
        },
    }
);
